// The editorial story that follows the 3D walk-through on the Venturist page

export const ventureOpening = {
  kicker: 'The Venturist Club',
  title: 'A club without an address.',
  body: 'Venturist gathers a small circle of members a few times a year, in places that are never announced. Each season is composed from the first arrival to the last hour of the night — and then disappears without a trace.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/b57c48d5-5d6c-4349-8ce0-a637e308681d.jpg',
  alt: 'A candlelit black-tie dinner on a cliffside terrace at night, a lit yacht on the sea below',
};

export type VentureSpread = {
  index: string;
  label: string;
  title: string;
  body: string;
  image: string;
  alt: string;
  inset: string;
  insetAlt: string;
  align: 'left' | 'right';
  layout?: 'split' | 'full';
};

export const ventureSpreads: VentureSpread[] = [
  {
    index: 'I',
    label: 'Arrival',
    title: 'Every arrival is arranged before it is noticed.',
    body: 'Aircraft, launches and cars meet each member at the door. No terminals, no queues, no names spoken aloud.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e0af2da4-37d0-4e1f-8c76-da6f981fe5f9.jpg',
    alt: 'The cabin of a private jet at sunrise, cream leather seats and walnut panels',
    inset: 'https://cdn.magicpatterns.com/patterns/generated-images/9dee4af9-582c-4df6-a1a3-f9864f4e283d.jpg',
    insetAlt: 'A chauffeur holding the rear door of a black car outside a grand hotel at night',
    align: 'left',
  },
  {
    index: 'II',
    label: 'Residence',
    title: 'Houses held, never listed.',
    body: 'Villas, palazzi and suites are prepared to every preference — and returned to silence when the season ends.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e4021ab5-ddf8-4848-8d8c-a6785f92e2f1.jpg',
    alt: 'A minimalist villa salon with travertine floors opening onto the Mediterranean',
    inset: 'https://cdn.magicpatterns.com/patterns/generated-images/45a56123-d72d-4433-b5e9-ea30d4dccc63.jpg',
    insetAlt: 'A Paris salon at dusk with tall windows facing the lit Eiffel Tower',
    align: 'right',
  },
  {
    index: 'III',
    label: 'Stillness',
    title: 'Hours that belong to no one else.',
    body: 'Coves closed until dusk, terraces without neighbours, mornings without a schedule.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/efcc7901-ba53-456d-920b-d2389b4b0c8a.jpg',
    alt: 'A secluded cove at golden hour seen from the swim platform of a classic yacht',
    inset: 'https://cdn.magicpatterns.com/patterns/generated-images/23ce8191-c0d0-4235-92f0-7e6f26bd5886.jpg',
    insetAlt: 'A steaming stone onsen among autumn maples at dusk',
    align: 'left',
    layout: 'full',
  },
  {
    index: 'IV',
    label: 'The Table',
    title: 'Where introductions become agreements.',
    body: 'Small tables, chosen company, nothing on record. For many members, the true reason for the journey.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/438f9d90-b1c8-49c1-a026-db43dfc6c8a8.jpg',
    alt: 'The aft deck of a superyacht at dusk, a table set for three',
    inset: 'https://cdn.magicpatterns.com/patterns/generated-images/3b1ad854-5cec-469e-b2b8-2295b9a690db.jpg',
    insetAlt: 'A candlelit tasting table in a vaulted wine cellar',
    align: 'right',
  },
];

// A filmstrip of a single season, scrolled sideways
export const ventureReel = {
  heading: 'One season, in frames',
  note: 'Scroll to move through',
  frames: [
    { time: '17:00', label: 'The Marina', title: 'Keys handed over at the quay', image: 'https://cdn.magicpatterns.com/patterns/generated-images/19d08ad4-119a-4e9e-a291-21be36a9e686.jpg', alt: 'A grand tourer on a marina quay beside a superyacht', shape: 'wide' },
    { time: '19:30', label: 'The Residence', title: 'A clifftop house, lights already low', image: 'https://cdn.magicpatterns.com/patterns/generated-images/ee27a00a-608b-4c4d-bf92-28b34626a7c2.jpg', alt: 'A cliffside villa with an infinity pool above the sea at sunset', shape: 'tall' },
    { time: '20:45', label: 'The Salon', title: 'Champagne facing the Eiffel Tower', image: 'https://cdn.magicpatterns.com/patterns/generated-images/45a56123-d72d-4433-b5e9-ea30d4dccc63.jpg', alt: 'A Paris salon at dusk facing the lit Eiffel Tower', shape: 'wide' },
    { time: '22:00', label: 'The Box', title: 'An opera opened for one evening', image: 'https://cdn.magicpatterns.com/patterns/generated-images/bb55534a-bb86-4a81-a8ea-8973d9b082cb.jpg', alt: 'A private opera box overlooking a lit stage', shape: 'tall' },
    { time: '00:30', label: 'Offshore', title: 'Anchored where the music carries', image: 'https://cdn.magicpatterns.com/patterns/generated-images/493d92cf-e2a4-4923-bd5d-29c66cf52b74.jpg', alt: 'Guests in evening wear on a superyacht deck at night', shape: 'wide' },
  ] as { time: string; label: string; title: string; image: string; alt: string; shape: 'wide' | 'tall' }[],
};

export const ventureQuote = {
  quote: 'Not attended. Arrived at.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/1035e490-4d04-41a5-ba97-aeb9b32614fd.jpg',
  alt: 'A candlelit black-tie dinner on a cliff terrace above the Riviera at night',
};

export const ventureGatherings = {
  heading: 'Where the club has gathered',
  note: 'Past seasons, in brief. The next is shared with members alone.',
  items: [
    { place: 'Saint-Tropez', title: 'A night aboard, anchored offshore', season: 'Summer', image: 'https://cdn.magicpatterns.com/patterns/generated-images/493d92cf-e2a4-4923-bd5d-29c66cf52b74.jpg', alt: 'Guests in evening wear on a superyacht deck at night' },
    { place: 'Vienna', title: 'An opera opened for one evening', season: 'Winter', image: 'https://cdn.magicpatterns.com/patterns/generated-images/bb55534a-bb86-4a81-a8ea-8973d9b082cb.jpg', alt: 'A private opera box overlooking a lit stage' },
    { place: 'Dubai', title: 'The polo final, from the marquee', season: 'Spring', image: 'https://cdn.magicpatterns.com/patterns/generated-images/d77b1e79-d8d7-44de-9615-87449339ec17.jpg', alt: 'A polo match at golden hour seen from a white marquee' },
    { place: 'Paris', title: 'A couture salon for thirty', season: 'Autumn', image: 'https://cdn.magicpatterns.com/patterns/generated-images/411a3879-1132-499e-8ca3-62a396fde316.jpg', alt: 'A model on the runway of an intimate couture show in a chandeliered salon' },
    { place: 'Courchevel', title: 'Supper in a hut above the pistes', season: 'Winter', image: 'https://cdn.magicpatterns.com/patterns/generated-images/96fcb92d-9e2a-48d3-ad4c-da72ca564a8f.jpg', alt: 'A candlelit table in a mountain hut with snowy peaks beyond' },
  ],
};
